import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  makeStyles,
  Paper,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ReplyIcon from "@material-ui/icons/Reply";
import SendIcon from "@material-ui/icons/Send";

import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    overflow: "hidden",
    borderRadius: 0,
    height: "100%",
    borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
  },
  messageList: {
    position: "relative",
    overflowY: "auto",
    height: "100%",
    ...theme.scrollbarStyles,
    backgroundColor: theme.mode === 'light' ? "#f2f2f2" : "#7f7f7f",
  },
  inputArea: {
    position: "relative",
    height: "auto",
  },
  input: {
    padding: "20px",
  },
  buttonSend: {
    margin: theme.spacing(1),
  },
  boxLeft: {
    padding: "10px 10px 5px",
    margin: "10px",
    position: "relative",
    backgroundColor: "#ffffff",
    color: "#303030",
    maxWidth: 300,
    borderRadius: 10,
    borderBottomLeftRadius: 0,
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  boxRight: {
    padding: "10px 10px 5px",
    margin: "10px 10px 10px auto",
    position: "relative",
    backgroundColor: "#dcf8c6",
    color: "#303030",
    textAlign: "right",
    maxWidth: 300,
    borderRadius: 10,
    borderBottomRightRadius: 0,
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  messageMenuButton: {
    position: "absolute",
    top: 4,
    right: 4,
    padding: 4,
    opacity: 0.3,
    transition: "opacity .2s ease",
    '&:hover': { opacity: 1 },
  },
  messageMenuButtonLeft: {
    position: "absolute",
    top: 4,
    left: 4,
    padding: 4,
    opacity: 0.3,
    transition: "opacity .2s ease",
    '&:hover': { opacity: 1 },
  },
  menuItemDanger: {
    color: '#d32f2f'
  }
}));

export default function ChatMessages({
  chat,
  messages,
  handleSendMessage,
  handleLoadMore,
  scrollToBottomRef,
  pageInfo,
  loading,
}) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
  const baseRef = useRef();

  const [contentMessage, setContentMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const isOwnMessage = (msg) => msg?.senderId === user.id;

  const handleOpenMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedMessage(null);
  };

  const handleReply = () => {
    // Placeholder para futura implementação de resposta/quote
    handleCloseMenu();
  };

  const handleEdit = async () => {
    // Placeholder: futura integração com modal de edição (MessageEditDeleteModal)
    // Poderia emitir um evento ou chamar callback futuro
    handleCloseMenu();
  };

  const handleDelete = async () => {
    try {
      // Endpoint hipotético: DELETE /chats/messages/:id (confirmar existência antes de ativar)
      // await api.delete(`/chats/messages/${selectedMessage.id}`);
      // Após remoção backend emitir evento socket – removendo localmente somente se necessário.
    } catch (err) {
      // console.error('Erro ao deletar mensagem', err);
    }
    handleCloseMenu();
  };

  const scrollToBottom = () => {
    if (baseRef.current) {
      baseRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const unreadMessages = (chat) => {
    if (chat !== undefined) {
      const currentUser = chat.users.find((u) => u.userId === user.id);
      return currentUser.unreads > 0;
    }
    return 0;
  };

  useEffect(() => {
    if (unreadMessages(chat) > 0) {
      try {
        api.post(`/chats/${chat.id}/read`, { userId: user.id });
      } catch (err) {
        console.error("Erro ao marcar mensagens como lidas:", err);
        // Não crítico, só loga o erro
      }
    }
    scrollToBottomRef.current = scrollToBottom;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = (e) => {
    const { scrollTop } = e.currentTarget;
    if (!pageInfo.hasMore || loading) return;
    if (scrollTop < 600) {
      handleLoadMore();
    }
  };

  return (
    <Paper className={classes.mainContainer}>
      <div onScroll={handleScroll} className={classes.messageList}>
        {Array.isArray(messages) &&
          messages.map((item, key) => {
            const own = item.senderId === user.id;
            return (
              <Box key={key} className={own ? classes.boxRight : classes.boxLeft}>
                <IconButton
                  size="small"
                  className={own ? classes.messageMenuButton : classes.messageMenuButtonLeft}
                  onClick={(e) => handleOpenMenu(e, item)}
                  aria-label="Ações da mensagem"
                >
                  <MoreVertIcon style={{ fontSize: 18 }} />
                </IconButton>
                <Typography variant="subtitle2">
                  {item.sender.name}
                </Typography>
                {item.message}
                <Typography variant="caption" display="block">
                  {datetimeToClient(item.createdAt)}
                </Typography>
              </Box>
            );
          })}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          keepMounted
          PaperProps={{ style: { minWidth: 180 } }}
        >
          <MenuItem onClick={handleReply} disabled={!selectedMessage}>
            <ListItemIcon style={{ minWidth: 32 }}>
              <ReplyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Responder" />
          </MenuItem>
          {selectedMessage && isOwnMessage(selectedMessage) && (
            <MenuItem onClick={handleEdit}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Editar" />
            </MenuItem>
          )}
          {selectedMessage && isOwnMessage(selectedMessage) && (
            <MenuItem onClick={handleDelete} className={classes.menuItemDanger}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Excluir" />
            </MenuItem>
          )}
        </Menu>
        <div ref={baseRef}></div>
      </div>
      <div className={classes.inputArea}>
        <FormControl variant="outlined" fullWidth>
          <Input
            multiline
            value={contentMessage}
            onKeyUp={(e) => {
              if (e.key === "Enter" && contentMessage.trim() !== "") {
                handleSendMessage(contentMessage);
                setContentMessage("");
              }
            }}
            onChange={(e) => setContentMessage(e.target.value)}
            className={classes.input}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={() => {
                    if (contentMessage.trim() !== "") {
                      handleSendMessage(contentMessage);
                      setContentMessage("");
                    }
                  }}
                  className={classes.buttonSend}
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
      </div>
    </Paper>
  );
}
